import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { WorkItemLogEvent } from '@robocorp/robocloud-orchestrator-interface';
import {
  Box,
  Column,
  Header,
  SkeletonLoader,
  SortDirection,
  Table,
  TableRowProps,
  TableSkeleton,
} from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { useFilterSettings, useParams } from '~/hooks';
import { useActivityQuery, useWorkItemLogQuery } from '~/queries';
import { Timestamp } from '~/components/time';
import { CellActorAvatar, CellRunDetails } from '~/components/tables';
import { SortRules, sortData } from '~/utils/searchUtils';
import { CellArrow } from '~/components/tables/cell/Arrow';

const Container = styled(Box)`
  overflow: auto;
`;

type RowData = WorkItemLogEvent;

type RowProps = {
  organizationShortName: string;
  workspaceShortName: string;
  processId: string;
  processRunId?: string;
  stepRunId?: string;
  step?: {
    name: string;
    template?: { name: string };
    order_number: number;
  };
};

const EventLogTableRow: FC<TableRowProps<RowData, RowProps>> = ({
  rowData: eventLog,
  props: { organizationShortName, workspaceShortName, processId, step, ...props },
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const processRunId = eventLog.processRunId ?? props.processRunId;
  const stepRunId = eventLog.stepRunId ?? props.stepRunId;
  const hasFullLink = processRunId !== undefined && stepRunId !== undefined;

  const link = hasFullLink
    ? `/${organizationShortName}/${workspaceShortName}/processes/${processId}/runs/${processRunId}/stepRuns/${stepRunId}`
    : undefined;

  return (
    <Table.Row onClick={link !== undefined ? () => navigate(link) : undefined}>
      <Table.Cell cellWrap="nowrap">
        <Timestamp seconds value={eventLog.loggedAt} />
      </Table.Cell>
      <Table.Cell>{eventLog.type ? t(eventLog.type) : '-'}</Table.Cell>
      <Table.Cell>
        {step?.name !== undefined ? (
          <CellRunDetails
            text={step.name}
            subText={step.template?.name}
            number={step.order_number}
          />
        ) : (
          '-'
        )}
      </Table.Cell>
      <Table.Cell cellWrap="nowrap">
        <CellActorAvatar actor={eventLog.actor} />
      </Table.Cell>
      <Table.Cell align="center">
        <CellArrow />
      </Table.Cell>
    </Table.Row>
  );
};

interface Props {
  workItemId: string;
  processId: string;
  processRunId?: string;
  stepId?: string;
  stepRunId?: string;
}

const defaultSortState: [string, SortDirection] = ['loggedAt', 'asc'];

const mapSortRules: SortRules<WorkItemLogEvent> = {
  loggedAt: { type: 'date', value: (item) => item.loggedAt },
};

const Content: FC<Props> = ({ workItemId, processId, processRunId, stepId, stepRunId }) => {
  const { t } = useTranslation();

  const { organizationShortName, workspaceShortName } = useParams<
    'organizationShortName' | 'workspaceShortName'
  >();

  const { data: { data: eventLogs = [] } = {}, isLoading } = useWorkItemLogQuery(workItemId);

  const { data: step } = useActivityQuery(
    { processId, activityId: stepId as string },
    { enabled: stepId !== undefined },
  );

  const columnDefinition = useMemo<Column[]>(
    () => [
      {
        title: t('workItem.eventLog.timestamp'),
        id: 'loggedAt',
        minWidth: 120,
        required: true,
        sortable: true,
      },
      {
        title: t('workItem.eventLog.action'),
        id: 'type',
      },
      {
        title: t('workItem.eventLog.step'),
        id: 'step',
        width: 140,
      },
      {
        title: t('workItem.eventLog.actor'),
        id: 'actor',
      },
      {
        id: 'actions',
        title: '',
        minWidth: 60,
        width: 60,
        resizable: false,
      },
    ],
    [t],
  );

  const {
    columns,
    sort: sortState,
    onSortChange,
  } = useFilterSettings('workItemEventLog', {
    columns: columnDefinition,
    sort: defaultSortState,
  });

  const sort = sortState || defaultSortState;
  const sortedData = sortData(mapSortRules, eventLogs, sort);

  const rowProps = useMemo(
    () => ({
      organizationShortName,
      workspaceShortName,
      processId,
      processRunId,
      stepRunId,
      step,
    }),
    [organizationShortName, processId, processRunId, stepRunId, step, workspaceShortName],
  );

  return (
    <Container py="$32" pl="$24" flex={1}>
      <Box pl="$16">
        <Header size="medium">
          <Header.Title title={t('workItem.eventLog.title')} />
        </Header>
      </Box>
      <SkeletonLoader skeleton={TableSkeleton} loading={isLoading}>
        <span>
          <Table
            columns={columns}
            data={sortedData}
            row={EventLogTableRow}
            rowProps={rowProps}
            sort={sort}
            onSort={onSortChange}
            rowCount="all"
          />
        </span>
      </SkeletonLoader>
    </Container>
  );
};

export const WorkItemLog = memo(Content);
